import React, { Dispatch, SetStateAction, useCallback } from 'react';
import { makeStyles } from 'design-system/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Slider from 'design-system/Slider';
import ToolSidebarButtonPopover from 'react-scale/components/scalers/ToolSidebar/ToolSidebarButtons/ButtonTypes/ToolSidebarButtonPopover';
import clsx from 'clsx';

const useStyles = makeStyles({
  popoverHorizontal: {
    width: 232,
    minHeight: 20,
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 20,
  },
  popoverVertical: {
    height: 232,
    minWidth: 20,
    padding: '16px 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
  },
  innerSlider: {
    flexGrow: 1,
  },
  button: {
    textTransform: 'lowercase',
    letterSpacing: 0,
    cursor: 'pointer',
    '&:hover': {
      transition: 'all 0.2s',
      opacity: 0.7,
      transform: 'scale(1.1)',
    },
  },
  resetColor: {
    color: 'rgba(0, 0, 0, 0.3)',
  },
});

interface VerticalSliderButtonProps {
  targetOpen: React.ReactElement;
  targetClosed: React.ReactElement;
  innerSliderIcon: React.ReactNode;
  value: number;
  onAdjust: Dispatch<SetStateAction<number>>;
  adjustmentMultiplier?: number;
  adjustmentOffset?: number;
  onReset?: () => void;
  onlyShowSlider?: boolean;
}

const VerticalSliderButton: React.FC<VerticalSliderButtonProps> = props => {
  const classes = useStyles();

  const {
    targetOpen,
    targetClosed,
    innerSliderIcon,
    value,
    onAdjust,
    adjustmentMultiplier,
    adjustmentOffset,
    onReset,
  } = props;

  const handleAdjust = useCallback(
    (newValue: any) => {
      const adjustedValue = newValue * (adjustmentMultiplier ?? 1) + (adjustmentOffset ?? 0);

      onAdjust(adjustedValue);
    },
    [onAdjust, adjustmentMultiplier, adjustmentOffset],
  );

  const handleReset = useCallback(() => {
    onReset ? onReset() : onAdjust(1);
  }, [onAdjust, onReset]);

  const handleMax = useCallback(() => {
    onAdjust(adjustmentMultiplier ?? 1);
  }, [onAdjust, adjustmentMultiplier]);

  const adjustedPosition = (value - (adjustmentOffset ?? 0)) / (adjustmentMultiplier ?? 1);

  const slider = (
    <div className={props.onlyShowSlider ? classes.popoverHorizontal : classes.popoverVertical}>
      <div className={classes.button} onClick={handleMax}>
        {innerSliderIcon}
      </div>

      <div className={classes.innerSlider}>
        <Slider
          vertical={!props.onlyShowSlider}
          position={adjustedPosition}
          onAdjust={handleAdjust}
        />
      </div>

      <div className={clsx(classes.button, classes.resetColor)} onClick={handleReset}>
        <FontAwesomeIcon icon="undo-alt" />
      </div>
    </div>
  );

  return props.onlyShowSlider ? (
    slider
  ) : (
    <ToolSidebarButtonPopover
      popover={slider}
      targetOpen={targetOpen}
      targetClosed={targetClosed}
    />
  );
};
export default VerticalSliderButton;
